import { useEffect, useMemo, useState } from 'react'
import { createComparisonPoster, createComparisonVideo, buildComparisonClips, comparisonVideoSupported, download } from './comparison-video'
import { modelSlug } from './format'
import type { LabResult } from './types'
import { useT } from './i18n'

export function ComparisonVideo({ pool, leftModel, rightModel, winner }: { pool: LabResult[]; leftModel: string; rightModel: string; winner?: string | null }) {
  const t = useT()
  const cv = t.compare
  const clips = useMemo(() => buildComparisonClips(pool, leftModel, rightModel), [pool, leftModel, rightModel])
  const [state, setState] = useState<'idle' | 'recording' | 'ready' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  const filename = `pelican-faceoff-${modelSlug(leftModel)}-vs-${modelSlug(rightModel)}`
  const generate = async () => {
    setState('recording')
    setMessage(cv.rendering)
    if (url) { URL.revokeObjectURL(url); setUrl(null) }
    try {
      const blob = await createComparisonVideo(clips, winner ?? undefined)
      setUrl(URL.createObjectURL(blob))
      setState('ready')
      setMessage(cv.ready)
    } catch (error) {
      setState('error')
      setMessage(error instanceof Error ? error.message : cv.videoError)
    }
  }
  const poster = async () => {
    try {
      const blob = await createComparisonPoster(clips, winner ?? undefined)
      download(blob, `${filename}.png`)
      setMessage(cv.posterDownloaded)
    } catch (error) {
      setState('error')
      setMessage(error instanceof Error ? error.message : cv.posterError)
    }
  }

  return (
    <section className="comparison-video" aria-labelledby="compare-video-title">
      <div>
        <p className="eyebrow">{cv.eyebrow}</p>
        <h3 id="compare-video-title">{cv.title}</h3>
        <p className="small muted">{clips.length ? cv.scenes(clips.length) : cv.needPelican}</p>
      </div>
      <div className="comparison-actions">
        <button type="button" className="btn btn-lime" disabled={!clips.length || state === 'recording'} onClick={generate}>
          {state === 'recording' ? cv.creating : cv.createVideo}
        </button>
        <button type="button" className="btn btn-ghost" disabled={!clips.length || state === 'recording'} onClick={poster}>{cv.downloadPoster}</button>
      </div>
      {message ? <p className={state === 'error' ? 'error small' : 'small muted'} aria-live="polite">{message}</p> : null}
      {url ? (
        <div className="comparison-preview">
          <video src={url} controls playsInline aria-label={cv.generatedAria} />
          <a className="btn btn-ink" href={url} download={`${filename}.webm`}>{cv.downloadWebm}</a>
        </div>
      ) : null}
      {!comparisonVideoSupported() ? <p className="small muted">{cv.noWebm}</p> : null}
    </section>
  )
}
